import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import _ from 'lodash';
import { green } from '@mui/material/colors';
import moment from 'moment';
import {
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CompletedIcon,
  GetApp as GetAppIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import config from '../../../utils/config';
import downloadInvoices from '../../../requests/downloadInvoices';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  statsItem: {
    alignItems: 'center',
    display: 'flex'
  },
  statsIcon: {
    marginRight: theme.spacing(1)
  }
}));

const InvoiceCard = ({ className, invoice, ...rest }) => {
  const classes = useStyles();
  const image = _.lowerCase(_.get(invoice, ['particulars', 0, 'category'], 'service')).replace(/\s+/g, '-');
  const subTotal = _.sumBy(_.get(invoice, 'particulars'), (p) => {
    return parseFloat(p.rate) * parseFloat(p.quantity);
  });
  const tax = _.round(_.sumBy(_.get(invoice, 'particulars'), (p) => {
    const price = parseFloat(p.rate) * parseFloat(p.quantity);
    return ((price * parseFloat(p.tax)) / 100);
  }), 2);
  const amount = subTotal + tax;
  const discountValue = (invoice.discount_type === 'Percent' ? _.round((amount * parseFloat(_.get(invoice, ['discount_value'], 0))) / 100, 2) : _.round(parseFloat(_.get(invoice, ['discount_value'], 0)), 2));
  const payable = amount - invoice.deposit - discountValue + invoice.other_charges;

  return (
    <Card
      className={clsx(classes.root, className)}
    >
      <CardContent>
        <Box
          display="flex"
          justifyContent="center"
          mb={3}
        >
          <Avatar
            alt="Invoice"
            src={`/static/images/cases/${image}.png`}
            variant="square"
          >
            {_.get(invoice, ['particulars', 0, 'category'], '')}
          </Avatar>
        </Box>
        <Typography
          align="center"
          color="textPrimary"
          gutterBottom
          variant="h4"
        >
          {`${invoice.invoice_id} / ${moment(invoice.invoice_date).format('MMM DD, YYYY')}`}
        </Typography>
        <Divider />
        <Typography
          align="center"
          color="textPrimary"
          variant="body1"
        >
          {`${invoice.first_name} ${invoice.last_name}`}
        </Typography>
        <Divider />
        <Typography
          align="center"
          color="textPrimary"
          variant="body2"
        >
          {`${invoice.email_id} / ${invoice.phone_no}`}
        </Typography>
        <Divider />
        {!_.isEmpty(invoice.company)
          && (
          <>
            <Typography
              align="center"
              color="textPrimary"
              variant="body2"
            >
              {invoice.company}
            </Typography>
            <Typography
              align="center"
              color="textPrimary"
              variant="body2"
            >
              {invoice.gst_no}
            </Typography>
            <Divider />
          </>
          )}
        <Divider />
        <Grid
          container
          justify="space-between"
        >
          <Grid item>
            <Typography color="textPrimary" variant="body1">Bill Amount:</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${subTotal}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' + '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${tax}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' = '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${amount}`}</Typography>
          </Grid>
        </Grid>
        <Grid
          container
          justify="space-between"
        >
          <Grid item>
            <Typography color="textPrimary" variant="body1">Payable:</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${amount}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' - '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${invoice.deposit}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' - '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${discountValue}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' + '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{`${config.currencySymbol}${invoice.other_charges}`}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textPrimary" variant="body2">{' = '}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textSecondary" variant="body1">{`${config.currencySymbol}${payable}`}</Typography>
          </Grid>
        </Grid>
        {/* <Divider />
        <Typography
          align="center"
          color="textPrimary"
          variant="body2"
        >
          {`Warranty Expires In: ${moment(invoice.invoice_date).fromNow()}`}
        </Typography> */}
      </CardContent>
      <Box flexGrow={1} />
      <Divider />
      <Box p={2}>
        <Grid
          container
          justify="space-between"
          spacing={2}
        >
          <Grid
            className={classes.statsItem}
            item
          >
            {invoice.status === 'Open'
              ? (
                <AccessTimeIcon
                  className={classes.statsIcon}
                  color="action"
                />
              )
              : (
                <CompletedIcon
                  className={classes.statsIcon}
                  style={{ color: green[500] }}
                />
              )}
            <Typography
              color="textSecondary"
              display="inline"
              variant="body2"
            >
              {invoice.status === 'Open' ? `Pending from ${moment(invoice.last_updated).fromNow()}` : `Updated ${moment(invoice.last_updated).fromNow()}`}
            </Typography>
          </Grid>
          <Grid
            className={classes.statsItem}
            item
          >
            {_.includes(rest.roles, 'preview-invoice')
              && (
              <PreviewIcon
                className={classes.statsIcon}
                color="action"
                onClick={() => rest.getPreviewInvoice(invoice.invoice_id)}
              />
              )}
            {_.includes(rest.roles, 'download-invoice')
              && (
              <GetAppIcon
                className={classes.statsIcon}
                color="action"
                onClick={() => downloadInvoices(invoice.invoice_id)}
              />
              )}
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

InvoiceCard.propTypes = {
  className: PropTypes.string,
  invoice: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  getPreviewInvoice: PropTypes.func
};

export default InvoiceCard;
